import type { ReactNode } from 'react';
import dynamic, { type LoaderComponent } from 'next/dynamic';
import { Loader as LoaderFn, LoaderOptions } from 'components/UI/Loader';
import type { PartialContext } from 'types/cms';
import ErrorBoundary from 'utils/ErrorBoundary';
import RenderHTMLComment from './ReactComment';

const loadComponent = (
  importFn: () => LoaderComponent<{ context: PartialContext; index?: number }>,
  loader?: () => ReactNode
) => {
  if (typeof loader === 'undefined') {
    return dynamic<{ context: PartialContext; index?: number }>(importFn, { ssr: true });
  }

  return dynamic<{ context: PartialContext; index?: number }>(importFn, { ssr: true, loading: loader });
};

const componentMap = {
  'article-index': loadComponent(() => import('./article/index').then((m) => m.ArticleIndex)),
  'group-ab-test-default': loadComponent(() => import('./group/ab/default').then((m) => m.GroupABTestDefault)),
  'group-grid-2-1': loadComponent(() => import('./group/grid/2-1').then((m) => m.GroupGrid21)),
  'group-grid-article': loadComponent(() => import('./group/grid/article').then((m) => m.GroupGridArticle)),
  'group-grid-combi-ad': loadComponent(() => import('./group/grid/combi-ad/').then((m) => m.GroupGridCombiAd)),
  'group-grid-default': loadComponent(() => import('./group/grid/default').then((m) => m.GroupGridDefault)),
  'group-grid-fixed-sidebar': loadComponent(() =>
    import('./group/grid/fixed-sidebar').then((m) => m.GroupGridFixedSidebar)
  ),
  'group-grid-header': loadComponent(() => import('./group/grid/header').then((m) => m.GroupGridHeader)),
  'group-grid-persona': loadComponent(() => import('./group/grid/persona').then((m) => m.GroupGridPersona)),
  'group-grid-playlist': loadComponent(() => import('./group/grid/playlist').then((m) => m.GroupGridPlaylist)),
  'group-grid-section': loadComponent(() => import('./group/grid/section').then((m) => m.GroupGridSection)),
  'group-grid-standard': loadComponent(() => import('./group/grid/auto-fill/').then((m) => m.GroupGridAutoFill)),
  'group-grid-topic': loadComponent(() => import('./group/grid/topic').then((m) => m.GroupGridTopic)),
  'group-preview-default': loadComponent(() => import('./group/preview/default').then((m) => m.GroupPreviewDefault)),
  'group-grid-viewport': loadComponent(() => import('./group/group-grid/viewport').then((m) => m.GroupGridViewport)),
  'page-index': loadComponent(() => import('./page/index').then((m) => m.IndexPage)),
  'widget-adslot-default': loadComponent(() => import('./widget/adslot/default').then((m) => m.WidgetAdslotDefault)),
  'widget-article-detail-author': loadComponent(() =>
    import('./widget/article-detail/author').then((m) => m.WidgetArticleDetailAuthor)
  ),
  'widget-article-detail-default': loadComponent(() =>
    import('./widget/article-detail/default').then((m) => m.WidgetArticleDetailDefault)
  ),
  'widget-article-detail-gallery': loadComponent(() =>
    import('./widget/article-detail/gallery').then((m) => m.WidgetArticleDetailGallery)
  ),
  'widget-article-detail-head': loadComponent(() =>
    import('./widget/article-detail/head').then((m) => m.WidgetArticleDetailHead)
  ),
  'widget-article-detail-no-render': loadComponent(() =>
    import('./widget/article-detail/no-render').then((m) => m.WidgetArticleDetailNoRender)
  ),
  'widget-article-detail-video': loadComponent(
    () => import('./widget/article-detail/video').then((m) => m.WidgetArticleDetailVideo),
    () => <LoaderFn type={LoaderOptions.VideoWithRelated} />
  ),
  'widget-autonative-default': loadComponent(() =>
    import('./widget/autonative/default').then((m) => m.WidgetAutonativeDefault)
  ),
  'widget-breadcrumb-default': loadComponent(() =>
    import('./widget/breadcrumb/default').then((m) => m.WidgetBreadcrumbDefault)
  ),
  'widget-epg-default': loadComponent(() => import('./widget/epg/default').then((m) => m.WidgetEpgDefault)),
  'widget-epg-episodes-preview': loadComponent(() =>
    import('./widget/epg/episodes-preview').then((m) => m.WidgetEpgEpisodesPreview)
  ),
  'widget-epg-rtl-plus-event': loadComponent(() =>
    import('./widget/epg/rtl-plus-event').then((m) => m.WidgetEpgRtlPlusEvent)
  ),
  'widget-epg-rtl-longform': loadComponent(() => import('./widget/epg/longform').then((m) => m.WidgetEpgLongform)),
  'widget-epg-rtl-catchup': loadComponent(() =>
    import('./widget/playlist/default').then((m) => m.WidgetPlaylistDefault)
  ),
  'widget-gallery-default': loadComponent(() => import('./widget/gallery/gallery').then((m) => m.WidgetGalleryDefault)),
  'widget-iframe-default': loadComponent(() => import('./widget/iframe/default').then((m) => m.WidgetIframeDefault)),
  'widget-integration-default': loadComponent(() =>
    import('./widget/integration/default').then((m) => m.WidgetIntegrationDefault)
  ),
  'widget-integration-tv-lottery-answers': loadComponent(() =>
    import('./widget/integration/tv-lottery-answers').then((m) => m.WidgetIntegrationTvLotteryAnswers)
  ),
  'widget-login-default': loadComponent(() => import('./widget/login/default').then((m) => m.WidgetLoginDefault)),
  'widget-menu-bannernavigation': loadComponent(() =>
    import('./widget/menu/bannerNavigation').then((m) => m.WidgetMenuBannerNavigation)
  ),
  'widget-menu-default': loadComponent(() => import('./widget/menu/default').then((m) => m.WidgetMenuDefault)),
  'widget-menu-footer': loadComponent(() => import('./widget/menu/footer').then((m) => m.WidgetMenuFooter)),
  'widget-menu-pill': loadComponent(() => import('./widget/menu/pill').then((m) => m.WidgetMenuPill)),
  'widget-menu-seobox': loadComponent(() => import('./widget/menu/seobox').then((m) => m.WidgetMenuSeobox)),
  'widget-pagination-default': loadComponent(() =>
    import('./widget/pagination/default').then((m) => m.WidgetPaginationDefault)
  ),
  'widget-picture-full': loadComponent(() => import('./widget/picture/full').then((m) => m.WidgetPictureFull)),
  'widget-picture-inline': loadComponent(() => import('./widget/picture/inline').then((m) => m.WidgetPictureInline)),
  'widget-playlist-default': loadComponent(() =>
    import('./widget/playlist/default').then((m) => m.WidgetPlaylistDefault)
  ),
  'widget-search-block': loadComponent(() => import('./widget/search/block').then((m) => m.WidgetSearchBlock)),
  'widget-search-default': loadComponent(() => import('./widget/search/default').then((m) => m.WidgetSearchDefault)),
  'widget-satisfaction-survey-default': loadComponent(() =>
    import('./widget/satisfaction-survey/default').then((m) => m.WidgetSatisfactionSurvey)
  ),
  'widget-seolinks-default': loadComponent(() =>
    import('./widget/seolinks/default').then((m) => m.WidgetSeoLinksDefault)
  ),
  'widget-series-authors': loadComponent(() => import('./widget/series/authors').then((m) => m.WidgetSeriesAuthors)),
  'widget-series-default': loadComponent(() => import('./widget/series/default').then((m) => m.WidgetSeriesDefault)),
  'widget-series-list': loadComponent(() => import('./widget/series/list').then((m) => m.WidgetSeriesList)),
  'widget-series-menu': loadComponent(() => import('./widget/series/menu').then((m) => m.WidgetSeriesMenu)),
  'widget-storyline_affiliate-default': loadComponent(() =>
    import('./widget/storyline-affiliate/default').then((m) => m.WidgetStorylineAffiliateDefault)
  ),
  'widget-storyline_date_and_author-default': loadComponent(() =>
    import('./widget/storyline-date-and-author/default').then((m) => m.WidgetStorylineDateAndAuthor)
  ),
  'widget-tagpage-index-default': loadComponent(() =>
    import('./widget/tagpage/index-default').then((m) => m.WidgetTagpageIndexDefault)
  ),
  'widget-tagpage-paging-default': loadComponent(() =>
    import('./widget/tagpage/paging-default').then((m) => m.WidgetTagpagePagingDefault)
  ),
  'widget-teaser-aufmacher': loadComponent(() =>
    import('./widget/teaser/aufmacher').then((m) => m.WidgetTeaserAufmacher)
  ),
  'widget-teaser-banner': loadComponent(() => import('./widget/teaser/banner').then((m) => m.WidgetTeaserBanner)),
  'widget-teaser-color': loadComponent(() => import('./widget/teaser/color').then((m) => m.WidgetTeaserColor)),
  'widget-teaser-highlight': loadComponent(() =>
    import('./widget/teaser/highlight').then((m) => m.WidgetTeaserHighlight)
  ),
  'widget-teaser-news-ticker': loadComponent(() =>
    import('./widget/teaser/news-ticker').then((m) => m.WidgetTeaserNewsTicker)
  ),
  'widget-teaser-playlist': loadComponent(() => import('./widget/teaser/playlist').then((m) => m.WidgetTeaserPlaylist)),
  'widget-teaser-rtl-plus': loadComponent(() => import('./widget/teaser/rtl-plus').then((m) => m.WidgetTeaserRtlPlus)),
  'widget-teaser-rtl-plus-ganze-folgen': loadComponent(() =>
    import('./widget/teaser/rtl-plus-ganze-folgen/').then((m) => m.WidgetTeaserRtlPlusFullEpisodes)
  ),
  'widget-teaser-rtl-plus-letzte-folgen': loadComponent(() =>
    import('./widget/teaser/rtl-plus-letzte-folgen').then((m) => m.WidgetTeaserRtlPlusLastEpisodes)
  ),
  'widget-teaser-rtl-plus-top5': loadComponent(() =>
    import('./widget/teaser/rtl-plus-top5').then((m) => m.WidgetTeaserRtlPlusTop5)
  ),
  'widget-teaser-search': loadComponent(() => import('./widget/teaser/search').then((m) => m.WidgetTeaserSearch)),
  'widget-teaser-small': loadComponent(() => import('./widget/teaser/small').then((m) => m.WidgetTeaserSmall)),
  'widget-teaser-small-aufmacher': loadComponent(() =>
    import('./widget/teaser/small-aufmacher').then((m) => m.WidgetTeaserSmallAufmacher)
  ),
  'widget-teaser-small-with-medium': loadComponent(() =>
    import('./widget/teaser/small-with-medium').then((m) => m.WidgetTeaserSmallWithMedium)
  ),
  'widget-teaser-standard': loadComponent(() => import('./widget/teaser/standard').then((m) => m.WidgetTeaserStandard)),

  'widget-teaser-text': loadComponent(() => import('./widget/teaser/text').then((m) => m.WidgetTeaserText)),
  'widget-teaser-video': loadComponent(() =>
    import('./widget/teaser/standard/standard').then((m) => m.WidgetTeaserStandard)
  ),
  'widget-topic_page_links-default': loadComponent(() =>
    import('./widget/topic-page-links/default').then((m) => m.WidgetTopicPageLinks)
  ),
  'widget-typography-persona': loadComponent(() =>
    import('./widget/typography/persona').then((m) => m.WidgetTypographyPersona)
  ),
  'widget-typography-gallery': loadComponent(() =>
    import('./widget/typography/gallery').then((m) => m.WidgetTypographyGallery)
  ),
  'widget-tools-calculator-allergy-test': loadComponent(() =>
    import('./widget/tools-calculator/allergy-test').then((m) => m.WidgetToolsCalculatorAllergyTest)
  ),
  'widget-tools-calculator-biological-age': loadComponent(() =>
    import('./widget/tools-calculator/biological-age').then((m) => m.WidgetToolsCalculatorBiologicalAge)
  ),
  'widget-tools-calculator-bmi': loadComponent(() =>
    import('./widget/tools-calculator/bmi').then((m) => m.WidgetToolsCalculatorBmi)
  ),
  'widget-tools-calculator-bmi-children': loadComponent(() =>
    import('./widget/tools-calculator/bmi-children').then((m) => m.WidgetToolsCalculatorBmiChildren)
  ),
  'widget-tools-calculator-date-of-birth': loadComponent(() =>
    import('./widget/tools-calculator/date-of-birth').then((m) => m.WidgetToolsCalculatorDateOfBirth)
  ),
  'widget-tools-calculator-love-oracle': loadComponent(() =>
    import('./widget/tools-calculator/love-oracle').then((m) => m.WidgetToolsCalculatorLoveOracle)
  ),
  'widget-tools-calculator-name-oracle': loadComponent(() =>
    import('./widget/tools-calculator/name-oracle').then((m) => m.WidgetToolsCalculatorNameOracle)
  ),
  'widget-video-detail': loadComponent(() => import('./widget/video/detail').then((m) => m.WidgetVideoDetail)),
  'widget-video-fast': loadComponent(() => import('./widget/video/fast').then((m) => m.WidgetVideoFast)),
  'widget-video-long': loadComponent(() => import('./widget/video/long').then((m) => m.WidgetVideoLong)),
};

type DynamicComponent = keyof typeof componentMap;

const constructDynamicName = (type: string, template: string, view?: string) => {
  const dynamicName = [type, template, view]
    .filter((str) => str !== undefined)
    .join('-')
    .toLowerCase();
  return dynamicName as DynamicComponent;
};

const Partials = ({ context, index }: { context: PartialContext; index?: number }) => {
  const { type, template, view } = context;
  const dynamicName = constructDynamicName(type, template, view);
  const Dynamic = componentMap[dynamicName];

  if (Dynamic) {
    return (
      <ErrorBoundary>
        <RenderHTMLComment widgetType={dynamicName} id={context?.id} />
        <Dynamic context={context} index={index} />
      </ErrorBoundary>
    );
  }

  return (
    <p>
      Cannot find <strong>{dynamicName}</strong>
    </p>
  );
};

export default Partials;
